import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { FaStar } from "react-icons/fa";
import { MdOutlineEmail, MdVerified } from "react-icons/md";
import { IoCall } from "react-icons/io5";
import ReadMoreText from "../ReadMoreText";
import Pagination from "../Pagination/Pagination";
import { HiUser } from "react-icons/hi2";


const toCount = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const pickPositiveMetric = (...values) => {
  for (const value of values) {
    if (value == null || value === "") continue;
    const num = Number(value);
    if (Number.isFinite(num) && num > 0) return num;
  }
  return 0;
};

const formatMedianPrice = (val) => {
  if (val == null || val === "") return "$0";
  const num =
    typeof val === "string"
      ? parseFloat(String(val).replace(/[^0-9.]/g, ""))
      : Number(val);
  if (Number.isNaN(num) || num === 0) return "$0";
  if (num >= 1_000_000) {
    return `$${(num / 1_000_000).toFixed(1).replace(/\.0$/, "")}m`;
  }
  if (num >= 10_000) return `$${Math.round(num / 1_000)}k`;
  return `$${num.toLocaleString("en-US")}`;
};

const getRegionLabel = (agent) => {
  const raw =
    agent?.agencyRegion ||
    agent?.suburb_area ||
    agent?.suburb ||
    agent?.state_region;
  const label = String(raw ?? "").trim();
  return label ? label.toUpperCase() : "ALL SUBURBS";
};

const AgentListEmptyState = ({ browseTo }) => (
  <div className="w-full bg-white border-[1.5px] border-[#EAEAEA] rounded-2xl flex flex-col items-center justify-center text-center px-6 py-10 sm:px-10 sm:py-12 min-h-[280px] shadow-sm">
    <div
      className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full bg-[#F3F6F9] flex items-center justify-center mb-5 ring-1 ring-[#EAEAEA]"
      aria-hidden
    >
      <HiUser className="text-[#097FE6]" size={36} />
    </div>
    <h3 className="text-lg sm:text-xl font-semibold text-[#000929] mb-2 max-w-md">
      No agents to show
    </h3>
    <p className="text-sm sm:text-base text-gray-500 max-w-lg mb-6 leading-relaxed">
      There aren&apos;t any agents listed for this area yet. Try the main find-agents page or check back later.
    </p>
    <Link to={browseTo} className="w-full sm:w-auto">
      <button
        type="button"
        className="w-full sm:w-auto rounded-full px-6 sm:px-8 py-2.5 sm:py-3 font-medium text-sm sm:text-base bg-[#097FE6] text-white hover:opacity-95 transition-opacity"
      >
        Find agents
      </button>
    </Link>
  </div>
);

const AgentListSearchEmptyState = ({ onClearSearch }) => (
  <div className="w-full bg-white border-[1.5px] border-[#EAEAEA] rounded-2xl flex flex-col items-center justify-center text-center px-6 py-10 sm:px-10 sm:py-12 min-h-[280px] shadow-sm">
    <div
      className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full bg-[#F3F6F9] flex items-center justify-center mb-5 ring-1 ring-[#EAEAEA]"
      aria-hidden
    >
      <HiUser className="text-[#097FE6]" size={36} />
    </div>
    <h3 className="text-lg sm:text-xl font-semibold text-[#000929] mb-2 max-w-md">
      No matching agents
    </h3>
    <p className="text-sm sm:text-base text-gray-500 max-w-lg mb-6 leading-relaxed">
      Nothing matches that search. Clear the search box or try different keywords.
    </p>
    {typeof onClearSearch === "function" ? (
      <button
        type="button"
        onClick={onClearSearch}
        className="w-full sm:w-auto rounded-full px-6 sm:px-8 py-2.5 sm:py-3 font-medium text-sm sm:text-base bg-[#097FE6] text-white hover:opacity-95 transition-opacity"
      >
        Clear search
      </button>
    ) : null}
  </div>
);

const AgentListErrorState = ({ onRetry, browseTo }) => (
  <div className="w-full bg-white border-[1.5px] border-[#EAEAEA] rounded-2xl flex flex-col items-center justify-center text-center px-6 py-10 sm:px-10 sm:py-12 min-h-[280px] shadow-sm">
    <div
      className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full bg-[#FEF2F2] flex items-center justify-center mb-5 ring-1 ring-[#FECACA]"
      aria-hidden
    >
      <span className="text-2xl sm:text-3xl font-semibold text-[#DC2626]" aria-hidden>
        !
      </span>
    </div>
    <h3 className="text-lg sm:text-xl font-semibold text-[#000929] mb-2 max-w-md">
      Couldn&apos;t load agents
    </h3>
    <p className="text-sm sm:text-base text-gray-500 max-w-lg mb-6 leading-relaxed">
      Something went wrong while fetching the list. Check your connection and try again.
    </p>
    {typeof onRetry === "function" ? (
      <button
        type="button"
        onClick={onRetry}
        className="w-full sm:w-auto rounded-full px-6 sm:px-8 py-2.5 sm:py-3 font-medium text-sm sm:text-base bg-[#097FE6] text-white hover:opacity-95 transition-opacity"
      >
        Try again
      </button>
    ) : (
      <Link to={browseTo} className="w-full sm:w-auto">
        <button
          type="button"
          className="w-full sm:w-auto rounded-full px-6 sm:px-8 py-2.5 sm:py-3 font-medium text-sm sm:text-base bg-[#097FE6] text-white hover:opacity-95 transition-opacity"
        >
          Find agents
        </button>
      </Link>
    )}
  </div>
);

const FindAgentCard = (props) => {
  const {
    AgentData,
    currentPage: propsCurrentPage,
    onPageChange: propsOnPageChange,
    pageSize: propsPageSize,
    fetchError = false,
    onRetry,
    agentSourceCount,
    onClearSearch,
    browseTo = "/find-agents",
  } = props;
  const navigate = useNavigate();
  const [internalPage, setInternalPage] = useState(1);
  const pageSize = propsPageSize ?? 6;
  const currentPage = propsCurrentPage ?? internalPage;
  const setCurrentPage = propsOnPageChange ?? setInternalPage;
  const list = Array.isArray(AgentData) ? AgentData : [];
  const sourceTotal = typeof agentSourceCount === "number" ? agentSourceCount : list.length;
  const firstPageIndex = (currentPage - 1) * pageSize;
  const lastPageIndex = firstPageIndex + pageSize;
  const currentData = list.slice(firstPageIndex, lastPageIndex);

  return (
    <>
      <div className="w-full max-w-full min-w-0 pb-8">
        {fetchError ? (
          <AgentListErrorState onRetry={onRetry} browseTo={browseTo} />
        ) : list.length === 0 && sourceTotal > 0 ? (
          <AgentListSearchEmptyState onClearSearch={onClearSearch} />
        ) : list.length === 0 ? (
          <AgentListEmptyState browseTo={browseTo} />
        ) : (
          currentData.map((i) => {
            const agentLogoSrc = i?.AgencySmallLogo;
            const agentImageSrc = i?.AgentImage;
            const goldCoastPropsSold = pickPositiveMetric(
              i?.PropertiesSoldAsLeadAgent,
              i?.property_sold,
              i?.propertiesSold,
            );
            const medianSoldPrice = formatMedianPrice(
              pickPositiveMetric(
                i?.MedianDaysPrice,
                i?.medianSoldPrice,
                i?.medianSoldPriceAsLeadAgent,
                i?.medianPrice,
              ),
            );
            const medianDaysAdvertised = toCount(i?.MediandaysAdvertised);
            const regionLabel = getRegionLabel(i);
            const verifiedReview = i?.VerifiedReview;
            const recentlySoldData = i?.RecentlySold;
            const navigateToAppraisal = () => {
              navigate(`/appraisal/${i?.id}`);
            };
            const navigateToAgentProfile = () => {
              navigate(`/agent-profile/${i?.id}`);
            };
            const reviews = i?.Review || [];
            const bestReview =
              reviews.find(r => Number(r.star) === 5)?.review ||
              reviews[0]?.review ||
              "No review yet.";
            return (
              <div key={i?.id} className="mb-4 sm:mb-6 rounded-xl sm:rounded-2xl overflow-hidden bg-white shadow-sm border border-[#E5E7EB] w-full max-w-full">
                <div
                  className={`cursor-pointer py-2 px-3 sm:px-4 min-h-[40px] flex items-center ${i?.primary_color ? "" : "bg-[#097FE6]"}`}
                  style={i?.primary_color ? { backgroundColor: i.primary_color } : undefined}
                  onClick={() => navigate(`/agency-profile/${i?.AgencyId}`)}
                >
                  {agentLogoSrc ? (
                    <img onClick={() => navigate(`/agency-profile/${i?.AgencyId}`)} src={agentLogoSrc} alt="" className="h-7 object-contain max-w-[140px] sm:max-w-[180px]" />
                  ) : (
                    <span onClick={() => navigate(`/agency-profile/${i?.AgencyId}`)} className="text-[#2E7D32] font-semibold text-xs sm:text-sm truncate cursor-pointer">{i?.AgentAbout || "Agency"}</span>
                  )}
                </div>

                <div className="p-3 sm:p-4 md:p-4 xl:p-6">
                  <div className="flex flex-col lg:flex-row gap-4 lg:gap-4 xl:gap-8">
                    <div className="w-full lg:w-[30%] flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-3 sm:gap-4 flex-shrink-0">
                      <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 min-w-0">
                        <LazyLoadImage
                          src={agentImageSrc}
                          alt=""
                          onClick={navigateToAgentProfile}
                          loading="lazy"
                          effect="blur"
                          className="w-14 h-14 sm:w-[76px] sm:h-[76px] rounded-full object-cover cursor-pointer shrink-0 mx-auto sm:mx-0"
                        />
                        <div className="min-w-0 flex-1 text-center sm:text-left">
                          <h2
                            onClick={navigateToAgentProfile}
                            className="text-[#333F48] font-bold text-base sm:text-lg md:text-xl cursor-pointer capitalize break-words"
                          >
                            {i?.AgentName}
                          </h2>
                          <p className="text-[#697684] text-xs sm:text-sm mt-0.5 line-clamp-2 sm:line-clamp-none break-words">
                            {i?.job_title || "Sales"} | {i?.AgentAbout}
                          </p>
                          {/* <div className="inline-flex items-center gap-1.5 mt-2 px-2 sm:px-2.5 py-1 rounded-md bg-[#FFD700] text-[#000929] text-[10px] sm:text-xs font-medium flex-wrap justify-center sm:justify-start">
                            <TfiMedallAlt className="text-[#000929] w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                            <span>2025 Top Agent in {suburbLabel.replace(/\s+/g, " ")}</span>
                          </div> */}
                          <div className="flex items-center gap-1 mt-2 text-xs sm:text-sm text-[#333F48] justify-center sm:justify-start flex-wrap">
                            <FaStar className="text-[#C2C823] w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                            <span className="font-medium">{i?.Rating ?? "0.0"}</span>
                            <span className="text-[#6B7280] font-medium">
                              ({typeof i?.Review === "number" ? i.Review : i?.Review?.length ?? 0} reviews)
                            </span>
                          </div>
                          <p className="text-[#000929] text-xs mt-2">{i?.Review?.length ?? 0} Reviews in last 12 months</p>
                          {/* <div className="flex flex-wrap gap-1.5 mt-3 min-w-0">
                            {defaultTags.map((tag) => (
                              <span key={tag} className="px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full font-medium bg-[#F3F4F6] text-[#374151] text-[10px] sm:text-xs">
                                {tag}
                              </span>
                            ))}
                          </div> */}
                        </div>
                      </div>
                    </div>

                    <div className="w-full lg:w-[70%] flex-1 min-w-0 space-y-3 sm:space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 lg:gap-2 xl:gap-6">
                        <div className="bg-[#F3F6F9] text-[#000929] rounded-lg sm:rounded-xl p-3 min-w-0 sm:col-span-2 lg:col-span-4">
                          <p className="text-xs font-semibold uppercase tracking-widest opacity-70">
                            {regionLabel}
                          </p>
                          <div className="grid grid-cols-3 gap-1.5 sm:gap-2 mt-2 sm:mt-3">
                            <div className="min-w-0">
                              <p className="font-bold text-base sm:text-lg md:text-xl lg:text-2xl truncate">
                                {medianSoldPrice}
                              </p>
                              <p className="opacity-70 text-[10px] sm:text-xs mt-0.5">
                                Median sold price
                              </p>
                            </div>
                            <div className="min-w-0">
                              <p className="font-bold text-base sm:text-lg md:text-xl lg:text-2xl truncate">
                                {medianDaysAdvertised}
                              </p>
                              <p className="opacity-70 text-[10px] sm:text-xs mt-0.5">
                                Median days advertised
                              </p>
                            </div>
                            <div className="min-w-0">
                              <p className="font-bold text-base sm:text-lg md:text-xl lg:text-2xl truncate">
                                {goldCoastPropsSold}
                              </p>
                              <p className="opacity-70 text-[10px] sm:text-xs mt-0.5">
                                Properties sold
                              </p>
                            </div>
                          </div>
                        </div>
                        {/* <div className="bg-[#F3F6F9] rounded-lg sm:rounded-xl p-3 min-w-0 sm:col-span-2 lg:col-span-1">
                          <p className="text-[#000929] text-xs font-semibold uppercase tracking-wide opacity-70">All suburbs</p>
                          <div className="mt-2 sm:mt-3">
                            <p className="text-[#000929] font-bold text-base sm:text-lg md:text-xl lg:text-2xl truncate">{allSuburbsPropsSold ?? "0"}</p>
                            <p className="text-[#6B7280] text-[10px] sm:text-xs mt-0.5">Properties sold</p>
                          </div>
                        </div> */}
                        <div className="flex flex-col justify-end gap-2 w-full sm:col-span-2 lg:col-span-2 order-first sm:order-none">
                          <button
                            type="button"
                            onClick={navigateToAppraisal}
                            className="w-full py-2.5 sm:py-3 px-3 sm:px-4 rounded-full bg-[#097FE6] text-white text-xs sm:text-sm font-medium hover:bg-[#097FE6]"
                          >
                            Request a free appraisal
                          </button>
                          <div className="flex gap-2 w-full">
                            <a
                              href={i?.phone ? `tel:${i.phone}` : "#"}
                              className="flex-1 min-w-0 flex items-center justify-center gap-1.5 sm:gap-2 py-2 sm:py-2.5 px-3 sm:px-4 rounded-full border-[1.5px] border-[#EAEAEA] bg-white text-[#333F48] text-xs sm:text-sm font-medium hover:bg-[#F9FAFB]"
                            >
                              <IoCall className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                              Call
                            </a>
                            <button
                              type="button"
                              onClick={navigateToAgentProfile}
                              className="flex-1 min-w-0 flex items-center justify-center gap-1.5 sm:gap-2 py-2 sm:py-2.5 px-3 sm:px-4 rounded-full border-[1.5px] border-[#EAEAEA] bg-white text-[#333F48] text-xs sm:text-sm font-medium hover:bg-[#F9FAFB]"
                            >
                              <MdOutlineEmail className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                              Enquire
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="bg-[#F9FAFB] rounded-lg p-3 sm:p-4">
                        <div className="flex flex-wrap items-center gap-2">
                          <FaStar className="text-[#C2C823] w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                          <span className="text-[#333F48] text-xs sm:text-sm font-medium">{i?.Rating ?? "0.0"}/5 – Rating from a buyer</span>
                          <span className="flex items-center gap-1 text-[#059669] text-[10px] sm:text-xs font-medium">
                            <MdVerified className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                            Verified review
                          </span>
                        </div>
                        <div className="mt-2 sm:mt-3">
                          <ReadMoreText
                            line={1}
                            text={bestReview || "No review yet."}
                            color="#3B82F6"
                          />
                        </div>
                      </div>

                      {recentlySoldData?.length > 0 && (
                        <div className="pt-2">
                          <h3 className="text-[#333F48] font-semibold text-sm sm:text-base mb-2 sm:mb-3">Properties Sold</h3>
                          <div className="flex gap-2 sm:gap-3 overflow-x-auto scrollBarHidden pb-1 -mx-1 px-1">
                            {recentlySoldData.slice(0, 6).map((d, idx) => (
                              <div
                                key={d?._id ?? idx}
                                className="relative shrink-0 w-20 h-20 min-[400px]:w-24 min-[400px]:h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-lg overflow-hidden cursor-pointer"
                                onClick={() => navigate(`/property-house/${d?._id}`)}
                              >
                                <img src={Array.isArray(d?.frontPageImg) && d.frontPageImg.length > 0 ? d.frontPageImg[0] : d?.frontPageImg} alt="" className="w-full h-full object-cover" />
                                <span className="absolute bottom-0 left-0 bg-[#C2C823] text-white text-[10px] sm:text-xs font-medium py-0.5 sm:py-1 px-1.5 sm:px-2 rounded-tr">
                                  Sold
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {!fetchError && list.length > 0 && (
        <div className="flex justify-center items-center pb-4 pt-2 px-2 sm:px-4 overflow-x-auto">
          <Pagination
            className="pagination-bar mb-2 max-w-full"
            currentPage={currentPage}
            totalCount={list.length}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </>
  );
};

export default FindAgentCard;
